/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import TertiaryButton from '../components/buttons/TertiaryButton';
import Chip from '../components/chip/Chip';
import {dlsColor} from '../theme/theme';

// defines the import validation prop type definitions ...
export interface ImportValidationProps {
  importValidation: string[];
  isVision?: boolean;
};

const useStyles = makeStyles(() => ({
  chipComponent : {
    display  : 'flex',
    flexWrap : 'wrap',
    rowGap   : '5px'
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '16px',
    fontFamily : 'Roboto',
    fontWeight : 400
  },
  button : {
    top : '-12px'
  }
}));

/**
 * @CancelButton
 *
 * The ImportValidation validates the uploaded devices.
 *
 * ```typescript
 *
 *    import {ImportValidation} from '@ion/dls/src/imports';
 *
 *    <ImportValidation importValidation={importValidation}  />
 *
 * ```
 *
 */
export const ImportValidation = ({
  importValidation,
  isVision = false
}: ImportValidationProps) => {
  const classes                 = useStyles();
  const path                    = 'common:editors.import';
  const [showMore, setShowMore] = useState(false);
  const displayMore             = !showMore ? importValidation?.slice(0, 20) : importValidation;

  const handleShowMore = useCallback(() => {
    setShowMore(!showMore);
  }, [showMore]);

  const getValidationMsg = () => {
    if (importValidation?.length === 1) {
      return isVision ? 'validateNameMsgSingular' : 'validateEsnMsgSingular';
    } else {
      return isVision ? 'validateNameMsgPlural' : 'validateEsnMsgPlural';
    }
  };

  return (
    (isVision && importValidation?.length > 100) ? (
      <div className={classes.chipComponent}>
        <span className={classes.errorMessage}>{translate(`${path}.validateMultipleMsg`)}</span>
      </div>
    ) : (<>
      <div className={classes.chipComponent}>
        <span className={classes.errorMessage}>{translate(`${path}.${isVision ? 'validateNameMsgStart' : 'validateEsnMsgStart'}`)}&nbsp;</span>
        <span className={classes.errorMessage}>{translate(`${path}.${getValidationMsg()}`)}&nbsp;</span>
        {displayMore?.map((esn) => (
          <Chip label={esn}
            size={'small'}
            maxWidth={false}
            borderRadius={'56px'}
            labelColor={dlsColor('grey2')}
            hoverDefault
            showCustomDeleteIcon
            noTextTransform />
        ))}
        {importValidation?.length > 20 && (
          <TertiaryButton onClick={handleShowMore} className={classes.button}>
            {translate(`${path}.${showMore ? 'showLess' : 'showMore'}`)}
          </TertiaryButton>
        )}
      </div>
      <span className={classes.errorMessage}>{translate(`${path}.${isVision ? 'validateNameMsgEnd' : 'validateEsnMsgEnd'}`)}</span>
    </>)
  );
};

export default ImportValidation;
